import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Check, CalendarIcon, ChevronRight, Loader2, MessageCircle } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "447340245823";

const PACKAGES = [
  {
    id: "small",
    name: "Small Package",
    price: 1000,
    guests: "0-50",
    description: "Perfect for intimate gatherings",
    features: ["£450 dry hire fee included", "One 30L keg", "12 Bottles of wine", "Setup, DJ, PA & Staff"]
  },
  {
    id: "medium",
    name: "Medium Package",
    price: 1550,
    guests: "50-100",
    description: "Ideal for medium events",
    features: ["£450 dry hire fee included", "One 50L keg", "24 Bottles of wine", "Setup, DJ, PA & Staff"]
  },
  {
    id: "large",
    name: "Large Package",
    price: 2100,
    guests: "100-150",
    description: "Perfect for grand celebrations",
    features: ["£450 dry hire fee included", "50L + 30L kegs", "36 Bottles of wine", "Setup, DJ, PA & Staff"]
  },
  {
    id: "dj_only",
    name: "DJ & Soundsystem Only",
    price: 500,
    guests: "Any",
    description: "Just the music - up to 5 hours",
    features: ["Professional DJ", "Full PA System", "Up to 5 hours", "Setup included"],
    isDJOnly: true
  }
];

const ADDONS = {
  cocktails: {
    small: { id: "cocktails_small", label: "Cocktails", price: 500 },
    medium: { id: "cocktails_medium", label: "Cocktails", price: 1000 },
    large: { id: "cocktails_large", label: "Cocktails", price: 1500 }
  },
  spirits: {
    small: { id: "spirits_small", label: "Spirits & Mixers", price: 200 },
    medium: { id: "spirits_medium", label: "Spirits & Mixers", price: 400 },
    large: { id: "spirits_large", label: "Spirits & Mixers", price: 600 }
  },
  wine: [
    { id: "wine_6", label: "Extra Wine (6 Bottles)", price: 140 },
    { id: "wine_12", label: "Extra Wine (12 Bottles)", price: 250 }
  ],
  kegs: [
    { id: "keg_30", label: "Extra 30L Keg", price: 180 },
    { id: "keg_50", label: "Extra 50L Keg", price: 230 }
  ]
};

const BookingPage = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [eventDate, setEventDate] = useState(null);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    event_type: "",
    venue_address: "",
    special_requests: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchUnavailableDates = async () => {
      try {
        const response = await axios.get(`${API}/unavailable-dates`);
        setUnavailableDates(response.data.map(d => d.date));
      } catch (error) {
        console.error("Error fetching unavailable dates:", error);
      }
    };
    fetchUnavailableDates();
    
    if (packageId) {
      const pkg = PACKAGES.find(p => p.id === packageId);
      if (pkg) {
        setSelectedPackage(pkg);
        if (pkg.isDJOnly) {
          setStep(3);
        } else {
          setStep(2);
        }
      }
    }
  }, [packageId]);

  const isDateUnavailable = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return unavailableDates.includes(dateStr);
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const getAllAddons = () => {
    const allAddons = [];
    if (selectedPackage && !selectedPackage.isDJOnly) {
      const size = selectedPackage.id;
      if (ADDONS.cocktails[size]) allAddons.push(ADDONS.cocktails[size]);
      if (ADDONS.spirits[size]) allAddons.push(ADDONS.spirits[size]);
      allAddons.push(...ADDONS.wine);
      allAddons.push(...ADDONS.kegs);
    }
    return allAddons;
  };

  const calculateTotal = () => {
    let total = selectedPackage?.price || 0;
    const allAddons = getAllAddons();
    allAddons.forEach(addon => {
      if (selectedAddons.includes(addon.id)) {
        total += addon.price;
      }
    });
    return total;
  };

  const getSelectedAddonDetails = () => {
    const details = [];
    const allAddons = getAllAddons();
    allAddons.forEach(addon => {
      if (selectedAddons.includes(addon.id)) {
        details.push(addon);
      }
    });
    return details;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSendRequest = () => {
    if (!formData.customer_name || !formData.customer_phone || !eventDate || !formData.event_type || !formData.venue_address) {
      toast.error("Please fill in all required fields (name, phone, date, event type, venue)");
      return;
    }

    setSubmitting(true);

    // Build WhatsApp message
    const addonDetails = getSelectedAddonDetails();
    const addonsList = addonDetails.length > 0
      ? addonDetails.map(a => `  - ${a.label}: £${a.price}`).join("\n")
      : "  None";

    const message = [
      `Hi! I'd like to book Charlesworth Club`,
      ``,
      `*Package:* ${selectedPackage.name} (£${selectedPackage.price})`,
      `*Event Date:* ${format(eventDate, "PPP")}`,
      `*Event Type:* ${formData.event_type}`,
      `*Venue:* ${formData.venue_address}`,
      ``,
      `*Add-ons:*`,
      addonsList,
      ``,
      `*Estimated Total:* £${calculateTotal().toLocaleString()}`,
      ``,
      `*Name:* ${formData.customer_name}`,
      formData.customer_email ? `*Email:* ${formData.customer_email}` : null,
      `*Phone:* ${formData.customer_phone}`,
      formData.special_requests ? `\n*Special Requests:* ${formData.special_requests}` : null,
    ].filter(Boolean).join("\n");

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    window.open(whatsappUrl, "_blank");
    setSubmitting(false);
    toast.success("Opening WhatsApp — we'll get back to you shortly!");
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="booking-page">
      <Navigation />

      <div className="pt-32 pb-24">
        <div className="container-custom">
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-4 mb-12" data-testid="booking-steps">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-10 h-10 flex items-center justify-center font-bold ${
                    step >= s ? 'bg-yellow-400 text-black' : 'bg-white/10 text-neutral-500'
                  }`}
                >
                  {s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-0.5 ${step > s ? 'bg-yellow-400' : 'bg-white/10'}`} />
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Step 1: Select Package */}
              {step === 1 && (
                <div data-testid="step-1">
                  <h2 className="heading-lg text-white mb-8">Select Your Package</h2>
                  <div className="space-y-4">
                    {PACKAGES.map((pkg) => (
                      <div
                        key={pkg.id}
                        onClick={() => setSelectedPackage(pkg)}
                        className={`surface-card p-6 cursor-pointer transition-all ${
                          selectedPackage?.id === pkg.id ? 'border-yellow-400' : ''
                        }`}
                        data-testid={`select-package-${pkg.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-white font-bold text-lg">{pkg.name}</h3>
                              <span className="text-xs bg-white/10 px-2 py-1 text-neutral-400">{pkg.guests} Guests</span>
                            </div>
                            <p className="text-neutral-400 text-sm">{pkg.description}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-black text-white">£{pkg.price.toLocaleString()}</span>
                            <div className={`w-6 h-6 mt-2 ml-auto border-2 ${
                              selectedPackage?.id === pkg.id 
                                ? 'bg-yellow-400 border-yellow-400' 
                                : 'border-white/30'
                            } flex items-center justify-center`}>
                              {selectedPackage?.id === pkg.id && <Check className="w-4 h-4 text-black" />}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={() => {
                        if (selectedPackage) {
                          if (selectedPackage.isDJOnly) {
                            setStep(3);
                          } else {
                            setStep(2);
                          }
                        }
                      }}
                      disabled={!selectedPackage}
                      className={`btn-primary ${!selectedPackage ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="continue-to-step-2"
                    >
                      {selectedPackage?.isDJOnly ? 'Continue to Details' : 'Continue'} <ChevronRight className="inline w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Customize Add-ons */}
              {step === 2 && (
                <div data-testid="step-2">
                  <h2 className="heading-lg text-white mb-2">Customize Your Package</h2>
                  <p className="text-body mb-8">Add cocktails, spirits, extra drinks and more to your event.</p>

                  {/* Cocktails */}
                  {selectedPackage && !selectedPackage.isDJOnly && ADDONS.cocktails[selectedPackage.id] && (
                    <div className="mb-8">
                      <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 bg-yellow-400/20 text-yellow-400 flex items-center justify-center text-sm">1</span>
                        Cocktails
                      </h3>
                      <p className="text-neutral-400 text-sm mb-4">Aperol Spritz, Pimms, Moscow Mule, Espresso Martini, G&T</p>
                      <div className="space-y-2">
                        {(() => {
                          const addon = ADDONS.cocktails[selectedPackage.id];
                          return (
                            <label
                              key={addon.id}
                              className={`surface-card p-4 flex items-center justify-between cursor-pointer ${
                                selectedAddons.includes(addon.id) ? 'border-yellow-400' : ''
                              }`}
                              data-testid={`addon-${addon.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <input type="checkbox" checked={selectedAddons.includes(addon.id)} onChange={() => toggleAddon(addon.id)} className="checkbox-custom" />
                                <span className="text-white">{addon.label}</span>
                              </div>
                              <span className="text-yellow-400 font-bold">+£{addon.price}</span>
                            </label>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Spirits */}
                  {selectedPackage && !selectedPackage.isDJOnly && ADDONS.spirits[selectedPackage.id] && (
                    <div className="mb-8">
                      <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 bg-yellow-400/20 text-yellow-400 flex items-center justify-center text-sm">2</span>
                        Spirits & Mixers
                      </h3>
                      <p className="text-neutral-400 text-sm mb-4">Vodka (Smirnoff), Gin (Bombay Sapphire), Rum (Bacardi) with mixers</p>
                      <div className="space-y-2">
                        {(() => {
                          const addon = ADDONS.spirits[selectedPackage.id];
                          return (
                            <label
                              key={addon.id}
                              className={`surface-card p-4 flex items-center justify-between cursor-pointer ${
                                selectedAddons.includes(addon.id) ? 'border-yellow-400' : ''
                              }`}
                              data-testid={`addon-${addon.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <input type="checkbox" checked={selectedAddons.includes(addon.id)} onChange={() => toggleAddon(addon.id)} className="checkbox-custom" />
                                <span className="text-white">{addon.label}</span>
                              </div>
                              <span className="text-yellow-400 font-bold">+£{addon.price}</span>
                            </label>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Wine */}
                  {selectedPackage && !selectedPackage.isDJOnly && (
                  <div className="mb-8">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <span className="w-8 h-8 bg-yellow-400/20 text-yellow-400 flex items-center justify-center text-sm">3</span>
                      Extra Wine
                    </h3>
                    <p className="text-neutral-400 text-sm mb-4">Premium white, red, rosé and bubbles</p>
                    <div className="space-y-2">
                      {ADDONS.wine.map((addon) => (
                        <label key={addon.id} className={`surface-card p-4 flex items-center justify-between cursor-pointer ${selectedAddons.includes(addon.id) ? 'border-yellow-400' : ''}`} data-testid={`addon-${addon.id}`}>
                          <div className="flex items-center gap-3">
                            <input type="checkbox" checked={selectedAddons.includes(addon.id)} onChange={() => toggleAddon(addon.id)} className="checkbox-custom" />
                            <span className="text-white">{addon.label}</span>
                          </div>
                          <span className="text-yellow-400 font-bold">+£{addon.price}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  )}

                  {/* Kegs */}
                  {selectedPackage && !selectedPackage.isDJOnly && (
                  <div className="mb-8">
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <span className="w-8 h-8 bg-yellow-400/20 text-yellow-400 flex items-center justify-center text-sm">4</span>
                      Extra Kegs
                    </h3>
                    <p className="text-neutral-400 text-sm mb-4">Estrella, Becks, Birra Moretti, Firebird beers</p>
                    <div className="space-y-2">
                      {ADDONS.kegs.map((addon) => (
                        <label key={addon.id} className={`surface-card p-4 flex items-center justify-between cursor-pointer ${selectedAddons.includes(addon.id) ? 'border-yellow-400' : ''}`} data-testid={`addon-${addon.id}`}>
                          <div className="flex items-center gap-3">
                            <input type="checkbox" checked={selectedAddons.includes(addon.id)} onChange={() => toggleAddon(addon.id)} className="checkbox-custom" />
                            <span className="text-white">{addon.label}</span>
                          </div>
                          <span className="text-yellow-400 font-bold">+£{addon.price}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  )}

                  <div className="mt-8 flex justify-between">
                    <button onClick={() => setStep(1)} className="btn-secondary" data-testid="back-to-step-1">Back</button>
                    <button onClick={() => setStep(3)} className="btn-primary" data-testid="continue-to-step-3">
                      Continue <ChevronRight className="inline w-4 h-4 ml-2" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Your Details */}
              {step === 3 && (
                <div data-testid="step-3">
                  <h2 className="heading-lg text-white mb-2">Your Details</h2>
                  <p className="text-body mb-8">Fill in your details and we'll start the conversation on WhatsApp.</p>

                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="label-text block mb-2">Full Name *</label>
                        <input type="text" name="customer_name" value={formData.customer_name} onChange={handleInputChange} className="input-dark" placeholder="John Smith" data-testid="input-name" />
                      </div>
                      <div>
                        <label className="label-text block mb-2">Email (optional)</label>
                        <input type="email" name="customer_email" value={formData.customer_email} onChange={handleInputChange} className="input-dark" placeholder="john@example.com" data-testid="input-email" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="label-text block mb-2">Phone *</label>
                        <input type="tel" name="customer_phone" value={formData.customer_phone} onChange={handleInputChange} className="input-dark" placeholder="+44 7123 456789" data-testid="input-phone" />
                      </div>
                      <div>
                        <label className="label-text block mb-2">Event Type *</label>
                        <select name="event_type" value={formData.event_type} onChange={handleInputChange} className="input-dark" data-testid="input-event-type">
                          <option value="">Select event type</option>
                          <option value="wedding">Wedding</option>
                          <option value="birthday">Birthday Party</option>
                          <option value="corporate">Corporate Event</option>
                          <option value="festival">Festival</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="label-text block mb-2">Event Date *</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="input-dark flex items-center justify-between" data-testid="input-date">
                            {eventDate ? format(eventDate, "PPP") : "Select a date"}
                            <CalendarIcon className="w-5 h-5 text-neutral-500" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#121212] border border-white/10" align="start">
                          <Calendar
                            mode="single"
                            selected={eventDate}
                            onSelect={setEventDate}
                            disabled={(date) => date < new Date() || isDateUnavailable(date)}
                            initialFocus
                            className="bg-[#121212]"
                            modifiers={{ unavailable: (date) => isDateUnavailable(date) }}
                            modifiersStyles={{ unavailable: { backgroundColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444', textDecoration: 'line-through' } }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div>
                      <label className="label-text block mb-2">Venue Address *</label>
                      <input type="text" name="venue_address" value={formData.venue_address} onChange={handleInputChange} className="input-dark" placeholder="Full venue address" data-testid="input-venue" />
                    </div>

                    <div>
                      <label className="label-text block mb-2">Special Requests (Optional)</label>
                      <textarea name="special_requests" value={formData.special_requests} onChange={handleInputChange} className="input-dark min-h-[100px]" placeholder="Any special requests or notes..." data-testid="input-requests" />
                    </div>
                  </div>

                  <div className="mt-8 flex justify-between">
                    <button onClick={() => setStep(selectedPackage?.isDJOnly ? 1 : 2)} className="btn-secondary" data-testid="back-to-step-2">Back</button>
                    <button 
                      onClick={handleSendRequest} 
                      disabled={submitting}
                      className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold py-4 px-8 uppercase tracking-wider text-sm transition-colors flex items-center gap-3"
                      data-testid="send-request-button"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <MessageCircle className="w-5 h-5" />
                      )}
                      Send Request via WhatsApp
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="surface-card p-6 sticky top-28" data-testid="order-summary">
                <h3 className="heading-md text-white mb-6">Order Summary</h3>
                
                {selectedPackage ? (
                  <>
                    <div className="border-b border-white/10 pb-4 mb-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-semibold">{selectedPackage.name}</p>
                          <p className="text-neutral-500 text-sm">{selectedPackage.guests} Guests</p>
                        </div>
                        <p className="text-white font-bold">£{selectedPackage.price.toLocaleString()}</p>
                      </div>
                    </div>

                    {getSelectedAddonDetails().length > 0 && (
                      <div className="border-b border-white/10 pb-4 mb-4">
                        <p className="label-text mb-3">Add-ons</p>
                        {getSelectedAddonDetails().map((addon) => (
                          <div key={addon.id} className="flex justify-between text-sm mb-2">
                            <span className="text-neutral-400">{addon.label}</span>
                            <span className="text-white">+£{addon.price}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-yellow-400 font-bold">Estimated Total</span>
                        <span className="text-yellow-400 font-bold text-xl">£{calculateTotal().toLocaleString()}</span>
                      </div>
                    </div>

                    <p className="text-neutral-500 text-xs mt-4">
                      Final pricing confirmed via WhatsApp. £450 deposit secures your date.
                    </p>
                  </>
                ) : (
                  <p className="text-neutral-500 text-center py-8">
                    Select a package to see your order summary
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingPage;
